import { FetchResult } from '@apollo/client';
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';

import { initRequest } from '../../api/request';
import {
  AuthChangePasswordNewUserInput,
  AuthSetPasswordInput,
  AuthToken,
} from '../../graphql.generated';
import paths from '../../paths.json';
import { setAuthorization } from '../GraphQLProvider/authLink';
import Loading from '../Loading';
import { useDefaultOrganizationDomains } from '../OrganizationProvider';

import {
  ChangePasswordMutation,
  useChangePasswordMutation,
} from './ChangePassword.generated';
import {
  CompleteAccountMigrationMutation,
  useCompleteAccountMigrationMutation,
} from './CompleteAccountMigration.generated';
import {
  ForgotPasswordMutation,
  useForgotPasswordMutation,
} from './ForgotPassword.generated';
import { LoginMutation, useLoginMutation } from './Login.generated';
import {
  LoginWithCodeMutation,
  useLoginWithCodeMutation,
} from './LoginWithCode.generated';
import {
  RefreshTokenMutation,
  useRefreshTokenMutation,
} from './RefreshToken.generated';
import {
  RequestAccountMigrationMutation,
  useRequestAccountMigrationMutation,
} from './RequestAccountMigration.generated';
import {
  RequestLoginCodeMutation,
  useRequestLoginCodeMutation,
} from './RequestLoginCode.generated';
import {
  ResetPasswordMutation,
  useResetPasswordMutation,
} from './ResetPassword.generated';
import {
  SetPasswordMutation,
  useSetPasswordMutation,
} from './SetPassword.generated';

type Authentication = {
  accessToken?: string;
  authenticated: boolean;
  /** The session came from an email login code and may still set a password without the current one. */
  canSetPassword: boolean;
  setPassword: (
    input: AuthSetPasswordInput,
  ) => Promise<FetchResult<SetPasswordMutation>>;
  forgotPassword: (
    email: string,
  ) => Promise<FetchResult<ForgotPasswordMutation>>;
  resetPassword: (
    email: string,
    password: string,
    token: string,
  ) => Promise<FetchResult<ResetPasswordMutation>>;
  changePassword: (
    input: AuthChangePasswordNewUserInput,
  ) => Promise<FetchResult<ChangePasswordMutation>>;
  completeAccountMigration: (
    email: string,
    code: string,
    newPassword: string,
  ) => Promise<FetchResult<CompleteAccountMigrationMutation>>;
  requestAccountMigration: (
    email: string,
  ) => Promise<FetchResult<RequestAccountMigrationMutation>>;
  login: (
    username: string,
    password: string,
  ) => Promise<FetchResult<LoginMutation>>;
  loginWithCode: (
    email: string,
    code: string,
  ) => Promise<FetchResult<LoginWithCodeMutation>>;
  logout: () => void;
  refreshToken?: string;
  renewToken: () => Promise<FetchResult<RefreshTokenMutation>>;
  requestLoginCode: (
    email: string,
  ) => Promise<FetchResult<RequestLoginCodeMutation>>;
};

type AuthenticationState = Pick<AuthToken, 'accessToken' | 'refreshToken'> & {
  expiresAt: number;
  /** Until when the access token carries the code-login claim (only the token minted by loginWithCode does). */
  codeLoginUntil?: number;
};

const contextError = () => {
  throw new Error('No <AuthenticationProvider />');
};

const contextErrorPromise = () => Promise.reject(contextError());

const AuthenticationContext = createContext<Authentication>({
  authenticated: false,
  canSetPassword: false,
  changePassword: contextErrorPromise,
  completeAccountMigration: contextErrorPromise,
  forgotPassword: contextErrorPromise,
  login: contextErrorPromise,
  loginWithCode: contextErrorPromise,
  logout: () => undefined,
  renewToken: contextErrorPromise,
  requestAccountMigration: contextErrorPromise,
  requestLoginCode: contextErrorPromise,
  resetPassword: contextErrorPromise,
  setPassword: contextErrorPromise,
});

export const useAuthentication = (): Authentication =>
  useContext(AuthenticationContext);

const getExpiresAt = (expiresIn: number) =>
  new Date(Date.now() + expiresIn * 1e3).getTime();

const AuthenticationProvider: FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const history = useHistory();
  const { surveyorUrl } = useDefaultOrganizationDomains();

  const [authentication, setAuthenticationState] = useState<
    AuthenticationState | undefined
  >(() => {
    const item = localStorage.getItem('authentication');

    if (!item) {
      return;
    }

    try {
      return JSON.parse(item) as AuthenticationState;
    } catch (e) {
      return undefined;
    }
  });

  const renewIn = useMemo(() => {
    return (
      authentication &&
      Math.max(0, authentication.expiresAt - Date.now() - 36e5)
    );
  }, [authentication]);

  const setAuthentication = useCallback(
    (
      result: Pick<AuthToken, 'accessToken' | 'expiresIn' | 'refreshToken'>,
      codeLogin = false,
    ) => {
      const expiresAt = getExpiresAt(result.expiresIn);
      setAuthenticationState({
        accessToken: result.accessToken,
        codeLoginUntil: codeLogin ? expiresAt : undefined,
        expiresAt,
        refreshToken: result.refreshToken,
      });
    },
    [],
  );

  const [_login] = useLoginMutation({
    onCompleted: (data) => {
      const token = data.login?.token;
      if (token) {
        setAuthentication(token);
      }
    },
  });
  const [_loginWithCode] = useLoginWithCodeMutation({
    onCompleted: (data) => {
      const token = data.loginWithCode?.token;
      if (token) {
        setAuthentication(token, true);
      }
    },
  });
  const [_setPassword] = useSetPasswordMutation({
    onCompleted: (data) => {
      const token = data.setPassword?.token;
      if (token) {
        setAuthentication(token);
      }
    },
  });
  const [_requestLoginCode] = useRequestLoginCodeMutation();
  const [_changePassword] = useChangePasswordMutation({
    onCompleted: (data) => {
      const token = data.changePasswordNewUser?.token;
      if (token) {
        setAuthentication(token);
      }
    },
  });
  const [_completeAccountMigration] = useCompleteAccountMigrationMutation({
    onCompleted: (data) => {
      const token = data.completeAccountMigration?.token;
      if (token) {
        setAuthentication(token);
      }
    },
  });
  const [_requestAccountMigration] = useRequestAccountMigrationMutation();
  const [_forgotPassword] = useForgotPasswordMutation();
  const [_resetPassword] = useResetPasswordMutation({
    onCompleted: (data) => {
      const token = data.resetPassword?.token;
      if (token) {
        setAuthentication(token);
      }
    },
  });
  const [_renewToken, { called: renewTokenCalled, loading: refreshing }] =
    useRefreshTokenMutation({
      onCompleted: (data) => {
        const token = data.refreshToken?.token;
        if (token) {
          setAuthentication(token);
        }
      },
    });

  const timeoutRef = useRef<number | null>(null);

  const authenticated = useMemo(() => {
    if (!authentication) {
      return false;
    }

    return Date.now() <= new Date(authentication.expiresAt).getTime();
  }, [authentication]);

  const login = useCallback(
    (username: string, password: string) =>
      _login({ variables: { input: { password, username } } }),
    [_login],
  );

  const loginWithCode = useCallback(
    (email: string, code: string) =>
      _loginWithCode({ variables: { code, email } }),
    [_loginWithCode],
  );

  const canSetPassword = useMemo(
    () =>
      !!authentication?.codeLoginUntil &&
      Date.now() < authentication.codeLoginUntil,
    [authentication],
  );

  const setPassword = useCallback(
    (input: AuthSetPasswordInput) => _setPassword({ variables: { input } }),
    [_setPassword],
  );

  const requestLoginCode = useCallback(
    (email: string) => _requestLoginCode({ variables: { email } }),
    [_requestLoginCode],
  );

  const changePassword = useCallback(
    (values: AuthChangePasswordNewUserInput) =>
      _changePassword({ variables: { input: values } }),
    [_changePassword],
  );

  const completeAccountMigration = useCallback(
    (email: string, code: string, newPassword: string) =>
      _completeAccountMigration({
        variables: { code, email, newPassword },
      }),
    [_completeAccountMigration],
  );

  const requestAccountMigration = useCallback(
    (email: string) => _requestAccountMigration({ variables: { email } }),
    [_requestAccountMigration],
  );

  const forgotPassword = useCallback(
    (email: string) => _forgotPassword({ variables: { email } }),
    [_forgotPassword],
  );

  const resetPassword = useCallback(
    (email: string, password: string, token: string) =>
      _resetPassword({ variables: { email, password, token } }),
    [_resetPassword],
  );

  const logout = useCallback(() => {
    setAuthenticationState(undefined);
    history.push(paths.login);
  }, [history]);

  const renewToken = useCallback(() => {
    timeoutRef.current = null;

    if (authentication?.refreshToken) {
      return _renewToken({
        variables: { input: { refreshToken: authentication?.refreshToken } },
      });
    }

    throw Error('No Refresh Token');
  }, [_renewToken, authentication?.refreshToken]);

  useEffect(() => {
    if (!authentication) {
      localStorage.removeItem('authentication');

      return;
    }

    localStorage.setItem('authentication', JSON.stringify(authentication));

    timeoutRef.current = window.setTimeout(
      () => void renewToken()?.catch(console.warn),
      renewIn,
    );

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [authentication, renewIn, renewToken]);

  useEffect(() => {
    if (authentication?.accessToken) {
      if (surveyorUrl) {
        initRequest({
          accessToken: authentication.accessToken,
          api: {
            surveyor: surveyorUrl,
          },
        });
      }
      setAuthorization(authentication.accessToken);
    }
  }, [authentication?.accessToken, surveyorUrl]);

  // Wait until refresh token finishes
  if ((!renewTokenCalled && renewIn === 0) || refreshing) {
    return <Loading />;
  }

  return (
    <AuthenticationContext.Provider
      value={{
        accessToken: authentication?.accessToken,
        authenticated,
        canSetPassword,
        changePassword,
        completeAccountMigration,
        forgotPassword,
        login,
        loginWithCode,
        logout,
        renewToken,
        requestAccountMigration,
        requestLoginCode,
        resetPassword,
        setPassword,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};

AuthenticationProvider.displayName = 'AuthenticationProvider';

export default AuthenticationProvider;
